import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import PhaseRing from "../components/PhaseRing";
import InsightCard from "../components/InsightCard";
import DailyRings from "../components/DailyRings";
import WeeklyReportCard from "../components/WeeklyReportCard";
import LogSheets from "../components/LogSheets";
import ReflectionSheet from "../components/ReflectionSheet";
import LegalFooter from "../components/LegalFooter";
import { getDashboard, getAppStreak, getSymptomPatterns } from "../lib/api";
import { usePushNotifications } from "../hooks/usePushNotifications";
import {
  Droplet, Moon, Smile, Activity, Heart, Sparkles,
  Apple, Briefcase, Flower2, Plus, ArrowUpRight, Stethoscope, MessageCircle, Flame, TrendingUp,
} from "lucide-react";
import { fmtDate } from "../lib/format";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  StarSticker, HeartSticker, CrescentSticker, BlobSticker, FlowerSticker, SparkBurst,
} from "../components/DecorativeShapes";

/* Companion microcopy by phase \u2014 invisible intelligence, not metrics-first */
const PHASE_NARRATIVE = {
  menstrual: {
    headline: "You're bleeding into a new chapter.",
    micro: "Energy may dip today. Prioritize warmth, slow movement, and recovery.",
    accent: "surface-rose",
    badgeColor: "chip-rose",
  },
  follicular: {
    headline: "Your energy is rebuilding.",
    micro: "A bright window for new ideas, deeper workouts and creative work.",
    accent: "surface-butter",
    badgeColor: "chip-butter",
  },
  ovulation: {
    headline: "You're at your radiant peak.",
    micro: "Social, magnetic and articulate. Lean into the connections you want.",
    accent: "surface-blush",
    badgeColor: "chip-blush",
  },
  luteal: {
    headline: "You're turning inward.",
    micro: "Slow analytical focus. Honor cravings. Bedtime an hour earlier helps.",
    accent: "surface-lavender",
    badgeColor: "chip-lavender",
  },
};

/* Cycle-synced life intelligence \u2014 the actual differentiator */
const LIFE_INTELLIGENCE = {
  menstrual: [
    { icon: Activity, label: "Movement",   tone: "surface-mint",     tip: "Gentle yoga or a long walk \u2014 nothing heavy."  },
    { icon: Briefcase, label: "Focus",     tone: "surface-cream",    tip: "Reflective work. Defer big decisions if you can." },
    { icon: Apple, label: "Nourish",       tone: "surface-butter",   tip: "Iron-rich foods. Warm broths beat cold meals." },
    { icon: Moon, label: "Rest",           tone: "surface-lavender", tip: "Earlier bedtime tonight. Magnesium helps cramps." },
  ],
  follicular: [
    { icon: Activity, label: "Movement",   tone: "surface-mint",     tip: "Try a new class \u2014 cardio & strength feel rewarding." },
    { icon: Briefcase, label: "Focus",     tone: "surface-cream",    tip: "Best week for new projects & deep learning." },
    { icon: Apple, label: "Nourish",       tone: "surface-butter",   tip: "Fresh produce, leafy greens, fermented foods." },
    { icon: Flower2, label: "Skin",        tone: "surface-blush",    tip: "Skin tends to glow now \u2014 keep routine simple." },
  ],
  ovulation: [
    { icon: Heart, label: "Connection",    tone: "surface-blush",    tip: "Confidence is high. Schedule the brave conversations." },
    { icon: Briefcase, label: "Focus",     tone: "surface-cream",    tip: "Peak verbal & social fluency \u2014 great for pitching." },
    { icon: Activity, label: "Movement",   tone: "surface-mint",     tip: "High-intensity & dance feel good. Stay hydrated." },
    { icon: Flower2, label: "Skin",        tone: "surface-butter",   tip: "Possible mid-cycle breakouts \u2014 keep skin clean." },
  ],
  luteal: [
    { icon: Briefcase, label: "Focus",     tone: "surface-cream",    tip: "Detail work & editing thrive. Avoid overcommitting." },
    { icon: Activity, label: "Movement",   tone: "surface-mint",     tip: "Pilates, swimming, slow strength \u2014 ease intensity." },
    { icon: Apple, label: "Nourish",       tone: "surface-butter",   tip: "Complex carbs, omega-3s. Reduce caffeine & sugar." },
    { icon: Moon, label: "Sleep",          tone: "surface-lavender", tip: "Melatonin may shift. Cool room, no screens after 10." },
  ],
};

const DEFAULT_NARRATIVE = {
  headline: "Let's learn your rhythm.",
  micro: "Log your first period and Knya will quietly start translating your body for you.",
  accent: "surface-cream",
  badgeColor: "chip-cream",
};

export default function HomePage({ profile }) {
  const [data, setData] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [reflectionOpen, setReflectionOpen] = useState(false);
  const [streak, setStreak] = useState(null);
  const [pattern, setPattern] = useState(null);
  const milestoneShown = useRef(false);
  const navigate = useNavigate();
  const { subscribe: subscribePush } = usePushNotifications();

  // Auto-request push permission once per install (250ms delay for smooth UX)
  useEffect(() => {
    const alreadySubscribed = (() => { try { return localStorage.getItem("knya.push.subscribed") === "1"; } catch { return false; } })();
    if (!alreadySubscribed) {
      const t = setTimeout(() => subscribePush(), 2500);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try { setData(await getDashboard()); } catch {}
      try {
        const s = await getAppStreak();
        setStreak(s);
        // Show milestone toast once per session
        if (s?.milestone_hit && !milestoneShown.current) {
          milestoneShown.current = true;
          const msgs = {
            7:   { title: "7-day streak!", desc: "You're building something real." },
            30:  { title: "30-day streak!", desc: "A full month of showing up for yourself." },
            100: { title: "100-day streak!", desc: "You're extraordinary. Truly." },
          };
          const m = msgs[s.milestone_hit];
          if (m) {
            setTimeout(() => toast(`🔥 ${m.title}`, { description: m.desc, duration: 5000 }), 800);
          }
        }
      } catch {}
      try {
        const p = await getSymptomPatterns();
        if (p?.has_patterns) setPattern(p);
      } catch {}
    })();
  }, [refresh]);

  const pred = data?.predictions || {};
  const latest = data?.latest || {};
  const firstName = (profile?.name || "").split(" ")[0];
  const today = new Date();
  const hr = today.getHours();
  const greet = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";

  const phaseKey = pred.has_data ? (pred.current_phase || "follicular") : null;
  const narrative = phaseKey ? PHASE_NARRATIVE[phaseKey] || DEFAULT_NARRATIVE : DEFAULT_NARRATIVE;
  const lifeCards = phaseKey ? (LIFE_INTELLIGENCE[phaseKey] || LIFE_INTELLIGENCE.follicular) : [];

  const PHASE_TINTS = {
    menstrual:    "bg-[#F88B8B]/20",
    follicular:   "bg-[#F5D867]/15",
    ovulation:    "bg-[#FF8FB8]/20",
    luteal_early: "bg-[#C3B1E1]/18",
    luteal:       "bg-[#A89CCC]/15",
    luteal_late:  "bg-[#8D84B3]/15",
  };
  const phaseTint = phaseKey ? (PHASE_TINTS[phaseKey] || "") : "";

  return (
    <div className="kanya-page-pad-bottom" data-testid="home-page">
      <PageHeader
        eyebrow={greet}
        title={firstName ? firstName : "Welcome"}
        subtitle={fmtDate(today.toISOString(), "EEEE, MMMM d")}
        right={
          <div className="flex items-center gap-2">
            {streak?.streak > 0 && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="flex items-center gap-1 bg-[hsl(var(--secondary))] rounded-full px-2.5 py-1 border border-[hsl(var(--border))]"
                data-testid="home-streak-chip"
                title={`${streak.streak}-day streak`}
              >
                <Flame size={13} className="text-orange-400" />
                <span className="text-[12px] font-extrabold text-[hsl(var(--foreground))]">{streak.streak}</span>
              </motion.div>
            )}
            <div className="relative w-10 h-10 rounded-full surface-ivory plush-shadow-sm flex items-center justify-center border border-[hsl(var(--border))]">
              <span className="font-editorial text-[18px] font-semibold">{firstName ? firstName[0]?.toUpperCase() : "K"}</span>
            </div>
          </div>
        }
      />

      <div className={`px-5 space-y-4 transition-colors duration-700 ${phaseTint} rounded-b-[32px] pb-4 -mt-2 pt-2`}>

        {/* === EDITORIAL PHASE HERO === */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-[28px] bg-secondary text-secondary-foreground editorial-shadow p-6"
          data-testid="home-phase-card"
        >
          {/* Soft brand glows */}
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />

          {pred.has_data ? (
            <>
              <div className="relative inline-flex items-center gap-1.5 bg-primary-foreground/15 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider">
                <Sparkles size={11} />
                {pred.phase_info?.title || phaseKey} · Day {pred.day_of_cycle}
              </div>

              <h2 className="relative text-[32px] leading-[1.05] mt-4 max-w-[280px] font-extrabold tracking-tight">
                {narrative.headline}
              </h2>
              <p className="relative text-[14px] opacity-80 mt-3 leading-relaxed max-w-[300px] font-medium">
                {narrative.micro}
              </p>

              {/* Phase ring + days-until */}
              <div className="relative mt-6 flex items-end justify-between gap-4">
                <PhaseRing
                  progress={pred.cycle_progress || 0}
                  day={pred.day_of_cycle}
                  phaseLabel={pred.phase_info?.title || phaseKey}
                  activePhase={phaseKey}
                  size={158}
                  stroke={14}
                  textColor="hsl(40 33% 97%)"
                />
                <div className="flex-1 min-w-0 pb-2">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold opacity-60">Next period</p>
                  <p className="text-[22px] leading-tight mt-1 font-extrabold tracking-tight">
                    {fmtDate(pred.next_period_date, "EEE, MMM d")}
                  </p>
                  <p className="text-[12px] opacity-70 mt-1 font-medium" data-testid="home-next-period-chip">
                    {pred.days_until_next_period <= 0
                      ? "Expected today"
                      : `in ${pred.days_until_next_period} day${pred.days_until_next_period === 1 ? "" : "s"}`}
                  </p>
                  <button
                    onClick={() => navigate("/cycle")}
                    className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold bg-primary text-primary-foreground rounded-full px-3 py-1.5 hover:opacity-90 transition"
                    data-testid="home-open-cycle"
                  >
                    Open cycle <ArrowUpRight size={12} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="relative inline-flex items-center gap-1.5 bg-primary-foreground/15 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider">
                <Sparkles size={11} /> Begin
              </div>
              <h2 className="relative text-[34px] leading-[1.04] mt-4 max-w-[300px] font-extrabold tracking-tight">
                {DEFAULT_NARRATIVE.headline}
              </h2>
              <p className="relative text-[14px] opacity-80 mt-3 leading-relaxed max-w-[300px] font-medium">
                {DEFAULT_NARRATIVE.micro}
              </p>
              <button
                onClick={() => setSheet("period")}
                className="relative mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-5 py-3 text-sm font-bold shadow-soft hover:opacity-95 transition"
                data-testid="home-log-period-cta"
              >
                <Droplet size={16} /> Log your period
              </button>
            </>
          )}
        </motion.div>

        {/* === DAILY AI INSIGHT === */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.12 }}
        >
          <InsightCard />
        </motion.div>

        {/* === DAILY RINGS (Move / Mind / Hydrate) === */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.18 }}
        >
          <DailyRings
            onTapMove={() => navigate("/wellness")}
            onTapMind={() => setReflectionOpen(true)}
            onTapHydrate={() => navigate("/wellness")}
          />
        </motion.div>

        {/* === WEEKLY REPORT === */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.22 }}
        >
          <WeeklyReportCard />
        </motion.div>

        {/* === SYMPTOM PATTERN NUDGE === */}
        <AnimatePresence>
          {pattern?.nudge && (
            <motion.div
              key="pattern-nudge"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.4, delay: 0.26 }}
              className="surface-lavender rounded-[22px] p-4 flex items-start gap-3"
              data-testid="home-pattern-nudge"
            >
              <div className="w-9 h-9 shrink-0 rounded-full bg-white/45 flex items-center justify-center">
                <TrendingUp size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-65">Pattern detected</p>
                <p className="text-[13px] font-medium mt-0.5 leading-snug">{pattern.nudge}</p>
                <button
                  onClick={() => navigate("/coach")}
                  className="mt-2 text-[11px] font-bold inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                  data-testid="home-pattern-nudge-cta"
                >
                  Ask Knya <ArrowUpRight size={10} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* === CYCLE-SYNCED LIFE INTELLIGENCE === */}
        {phaseKey && lifeCards.length > 0 && (
          <section data-testid="home-life-intelligence">
            <div className="flex items-end justify-between px-1 pt-2 pb-1">
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--muted-foreground))]">
                Today, your body suggests
              </p>
            </div>

            <div className="bento mt-2">
              {/* Irregular layout: 1st card 7-wide, 2nd 5-wide, 3rd 5-wide, 4th 7-wide */}
              {lifeCards.map((card, idx) => {
                const Icon = card.icon;
                const isWide = idx === 0 || idx === 3;
                const span = isWide ? "b-7" : "b-5";
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * idx + 0.2 }}
                    className={`${span} ${card.tone} plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden`}
                    data-testid={`home-life-card-${card.label.toLowerCase()}`}
                  >
                    {idx === 0 && <BlobSticker size={70} color="#FFFFFF" className="absolute -top-6 -right-6 opacity-40" />}
                    {idx === 1 && <StarSticker size={28} color="#FFFFFF" className="absolute top-3 right-3 opacity-60" />}
                    {idx === 2 && <SparkBurst size={26} color="#FFFFFF" className="absolute top-3 right-3 opacity-50" />}
                    {idx === 3 && <CrescentSticker size={36} color="#FFFFFF" className="absolute -top-2 -right-2 opacity-50" />}
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-white/45 flex items-center justify-center mb-2">
                        <Icon size={16} />
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.24em] font-semibold opacity-65">{card.label}</p>
                      <p className="text-[13.5px] font-body mt-1 leading-snug font-medium">{card.tip}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}

        {/* === QUICK LOG ROW \u2014 compact, less dense === */}
        <section className="pt-2" data-testid="home-quick-log">
          <div className="flex items-end justify-between px-1 pb-1.5">
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--muted-foreground))]">
              Quick log
            </p>
            <button
              onClick={() => navigate("/wellness")}
              className="text-[11px] font-semibold text-[hsl(var(--plum))] inline-flex items-center gap-0.5 hover:gap-1 transition-all"
              data-testid="home-open-wellness"
            >
              All wellness <ArrowUpRight size={11} />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2.5">
            {[
              { key: "mood",    Icon: Smile,    label: "Mood",   tone: "surface-blush", testid: "home-quick-log-mood-button" },
              { key: "sleep",   Icon: Moon,     label: "Sleep",  tone: "surface-lavender", testid: "home-quick-log-sleep-button" },
              { key: "period",  Icon: Droplet,  label: "Period", tone: "surface-rose", testid: "home-quick-log-period-button" },
              { key: "fitness", Icon: Activity, label: "Move",   tone: "surface-mint", testid: "home-quick-log-fitness-button" },
            ].map(({ key, Icon, label, tone, testid }) => (
              <motion.button
                key={key}
                whileTap={{ scale: 0.94 }}
                onClick={() => setSheet(key)}
                className={`${tone} plush-shadow-sm rounded-[22px] py-3.5 px-2 flex flex-col items-center gap-1.5`}
                data-testid={testid}
              >
                <div className="w-9 h-9 rounded-full bg-white/45 flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <span className="text-[11px] font-semibold">{label}</span>
              </motion.button>
            ))}
          </div>

          {/* Skin + Nutrition as wide secondary */}
          <div className="grid grid-cols-2 gap-2.5 mt-2.5">
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setSheet("skin")}
              className="surface-sky plush-shadow-sm rounded-[22px] py-3 px-4 flex items-center justify-between"
              data-testid="home-quick-log-skin-button"
            >
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-semibold">Skin</p>
                <p className="text-[13px] font-semibold capitalize mt-0.5">{latest.skin?.condition || "Log"}</p>
              </div>
              <Sparkles size={16} className="opacity-70" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setSheet("nutrition")}
              className="surface-butter plush-shadow-sm rounded-[22px] py-3 px-4 flex items-center justify-between"
              data-testid="home-quick-log-nutrition-button"
            >
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-semibold">Nourish</p>
                <p className="text-[13px] font-semibold mt-0.5">Add meal</p>
              </div>
              <Apple size={16} className="opacity-70" />
            </motion.button>
          </div>
        </section>

        {/* === DISCOVER: Care + Community === */}
        <section className="pt-1" data-testid="home-discover">
          <div className="flex items-end justify-between px-1 pb-1.5">
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[hsl(var(--muted-foreground))]">
              Discover
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/care")}
              className="surface-mint plush-shadow-sm rounded-[22px] py-3.5 px-4 flex items-start justify-between gap-2 text-left"
              data-testid="home-discover-care"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-semibold">Care</p>
                <p className="text-[13px] font-semibold mt-0.5">Find a specialist</p>
                <p className="text-[11px] opacity-65 mt-0.5 leading-snug">Curated clinicians</p>
              </div>
              <Stethoscope size={16} className="opacity-75 shrink-0 mt-0.5" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/community")}
              className="surface-blush plush-shadow-sm rounded-[22px] py-3.5 px-4 flex items-start justify-between gap-2 text-left"
              data-testid="home-discover-community"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-semibold">Community</p>
                <p className="text-[13px] font-semibold mt-0.5">You're not alone</p>
                <p className="text-[11px] opacity-65 mt-0.5 leading-snug">Anonymous space</p>
              </div>
              <MessageCircle size={16} className="opacity-75 shrink-0 mt-0.5" />
            </motion.button>
          </div>
        </section>

        {/* === NIGHTLY REFLECTION ENTRY === */}
        <motion.button
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => setReflectionOpen(true)}
          className="w-full bg-secondary text-secondary-foreground editorial-shadow rounded-[28px] p-5 mt-2 text-left relative overflow-hidden"
          data-testid="home-reflection-cta"
        >
          <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-primary/25 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 bg-primary-foreground/15 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider">
              <Sparkles size={11} />
              {hr >= 18 ? "Tonight" : "When the day winds down"}
            </div>
            <h3 className="text-[24px] leading-tight mt-3 max-w-[280px] font-extrabold tracking-tight">
              How was today, really?
            </h3>
            <p className="text-[13px] opacity-75 mt-2 max-w-[300px] font-medium">
              Three soft prompts. Knya quietly weaves your reflections into pattern intelligence over time.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold bg-primary text-primary-foreground rounded-full px-4 py-2">
              Begin reflection <ArrowUpRight size={14} />
            </div>
          </div>
        </motion.button>

        {/* Subtle Bloom footer line */}
        <div className="pt-3 pb-1 flex items-center justify-center">
          <p className="text-[12px] text-muted-foreground italic font-medium">
            Your body, translated into intelligence.
          </p>
        </div>

        <LegalFooter />
      </div>

      <LogSheets open={sheet} onClose={() => setSheet(null)} onLogged={() => setRefresh((r) => r + 1)} />
      <ReflectionSheet open={reflectionOpen} onClose={() => setReflectionOpen(false)} profile={profile} />
    </div>
  );
}
